import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'

import api from 'balkerne-core/api'
import { Stack, Button, TextField, Typography, Grid, Card, CardContent, Paper, InputAdornment } from '@mui/material'
import Page from '../components/Page'
import { useHistory, useParams } from 'react-router-dom'
import { Controller, useForm } from 'react-hook-form'
import DrawMap from '../components/userManagement/DrawMap'
import { useSnackbar } from 'notistack'
import { useLocation } from '../hooks/locations'
import { useQueryClient } from 'react-query'
import { FeatureGate } from 'balkerne-core/roles'
import LocalisedNumberTextField from '../components/LocalisedNumberTextField'
import { useCurrentUser } from '../hooks/users'
import siteMap from '../siteMap'

type FormValues = {
  name: string
  reference: string
  structure_value: number | null
  contents_value: number | null
  revenue: number | null
}

const emptyToNullNumber = (value: unknown, originalValue: unknown) => {
  if (originalValue === '' || originalValue === null || originalValue === undefined) return null
  return value
}

const toNumberOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined) return null
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : null
}

const geometryToFeatureCollection = (geometry: any): GeoJSON.FeatureCollection | null => {
  if (!geometry) return null
  if (geometry.type === 'FeatureCollection') return geometry as GeoJSON.FeatureCollection
  if (geometry.type === 'Feature') {
    return { type: 'FeatureCollection', features: [geometry] } as GeoJSON.FeatureCollection
  }
  if (geometry.type === 'Polygon') {
    return {
      type: 'FeatureCollection',
      features: [{ type: 'Feature', properties: {}, geometry }],
    } as GeoJSON.FeatureCollection
  }
  if (geometry.type === 'MultiPolygon') {
    return {
      type: 'FeatureCollection',
      features: (geometry.coordinates ?? []).map((coords: any) => ({
        type: 'Feature',
        properties: {},
        geometry: { type: 'Polygon', coordinates: coords },
      })),
    } as GeoJSON.FeatureCollection
  }
  return null
}

const normalizeFeatureCollection = (
  fc: GeoJSON.FeatureCollection | null | undefined,
): GeoJSON.FeatureCollection | null => {
  if (!fc) return null
  return {
    type: 'FeatureCollection',
    features: (fc.features ?? []).map(f => ({
      type: 'Feature',
      properties: {},
      geometry: (f as any)?.geometry,
    })),
  } as GeoJSON.FeatureCollection
}

const eqNumber = (a: number | null | undefined, b: number | null | undefined) => {
  if (a === null && b === null) return true
  if (a === undefined && b === undefined) return true
  if (a === null || a === undefined || b === null || b === undefined) return false
  return Math.abs(a - b) < 1e-9
}

const EditProperty = () => {
  const history = useHistory()
  const { id: propertyId } = useParams()
  const getLocationParams = useMemo(() => ({ geojson: true }), [])
  const { data: property, isLoading, isError, error } = useLocation(propertyId, getLocationParams)
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()
  const { currencyISO, currencySymbol } = useCurrentUser()
  const initialGeometry = useMemo(() => geometryToFeatureCollection(property?.geometry), [property])
  const initialGeometryRef = useRef<GeoJSON.FeatureCollection | null>(null)
  const [confirmedGeometry, setConfirmedGeometry] = useState<GeoJSON.FeatureCollection | null>(null)
  const [isEditingGeometry, setIsEditingGeometry] = useState(false)
  const draftGeometryRef = useRef<GeoJSON.FeatureCollection | null>(null)
  const [draftHasGeometry, setDraftHasGeometry] = useState(false)

  const schema = yup
    .object({
      name: yup.string().trim().required('Name is required'),
      reference: yup.string().trim().required('Reference number is required'),
      structure_value: yup
        .number()
        .nullable()
        .transform(emptyToNullNumber)
        .typeError('Structure value must be a number')
        .min(0, 'Structure value must be at least 0'),
      contents_value: yup
        .number()
        .nullable()
        .transform(emptyToNullNumber)
        .typeError('Contents value must be a number')
        .min(0, 'Contents value must be at least 0'),
      revenue: yup
        .number()
        .nullable()
        .transform(emptyToNullNumber)
        .typeError('Revenue must be a number')
        .min(0, 'Revenue must be at least 0'),
    })
    .required()

  const {
    register,
    handleSubmit: formSubmit,
    formState: { isSubmitting, errors },
    reset,
    control,
    watch,
  } = useForm<FormValues>({
    mode: 'onChange',
    reValidateMode: 'onBlur',
    defaultValues: {
      name: '',
      reference: '',
      structure_value: null,
      contents_value: null,
      revenue: null,
    },
    resolver: yupResolver(schema),
  })

  const initialRef = useRef<FormValues | null>(null)
  const loadedPropertyIdRef = useRef<any>(null)

  useEffect(() => {
    if (!property) return
    if (loadedPropertyIdRef.current === property.id) return
    loadedPropertyIdRef.current = property.id

    const defaults: FormValues = {
      name: property?.name ?? '',
      reference: property?.property_customer_reference ?? '',
      structure_value: toNumberOrNull(property?.insurance?.structure),
      contents_value: toNumberOrNull(property?.insurance?.contents),
      revenue: toNumberOrNull(property?.insurance?.revenue),
    }
    initialRef.current = defaults
    reset(defaults)
    initialGeometryRef.current = initialGeometry ?? null
    setConfirmedGeometry(initialGeometry ?? null)
    setIsEditingGeometry(false)
    draftGeometryRef.current = null
    setDraftHasGeometry(false)
  }, [initialGeometry, property, reset])

  const handleMapError = useCallback((error: string) => enqueueSnackbar(error, { variant: 'error' }), [enqueueSnackbar])

  const currentValues = watch()
  const baselineValues = initialRef.current
  const currentGeometry = confirmedGeometry ?? initialGeometry ?? null

  const hasFieldChanges =
    baselineValues !== null &&
    ((currentValues?.name ?? '').trim() !== (baselineValues?.name ?? '').trim() ||
      (currentValues?.reference ?? '').trim() !== (baselineValues?.reference ?? '').trim() ||
      !eqNumber(currentValues?.structure_value, baselineValues?.structure_value) ||
      !eqNumber(currentValues?.contents_value, baselineValues?.contents_value) ||
      !eqNumber(currentValues?.revenue, baselineValues?.revenue))

  const hasGeometryChanges =
    baselineValues !== null &&
    JSON.stringify(normalizeFeatureCollection(currentGeometry)) !==
      JSON.stringify(normalizeFeatureCollection(initialGeometryRef.current))

  const hasChanges = hasFieldChanges || hasGeometryChanges

  const startGeometryEdit = useCallback(() => {
    draftGeometryRef.current = currentGeometry
    setDraftHasGeometry((currentGeometry?.features?.length ?? 0) > 0)
    setIsEditingGeometry(true)
  }, [currentGeometry])

  const discardGeometryEdit = useCallback(() => {
    draftGeometryRef.current = null
    setDraftHasGeometry(false)
    setIsEditingGeometry(false)
  }, [])

  const handleGeometryDraftChange = useCallback((featureCollection: GeoJSON.FeatureCollection | null) => {
    draftGeometryRef.current = featureCollection
    setDraftHasGeometry((featureCollection?.features?.length ?? 0) > 0)
  }, [])

  const confirmGeometryEdit = useCallback(() => {
    const draft = draftGeometryRef.current
    if ((draft?.features?.length ?? 0) === 0) {
      enqueueSnackbar('Please draw a property boundary before confirming.', { variant: 'warning' })
      return
    }
    const normalized = normalizeFeatureCollection(draft)
    setConfirmedGeometry(normalized)
    setIsEditingGeometry(false)
    draftGeometryRef.current = null
    setDraftHasGeometry(false)
  }, [enqueueSnackbar])

  const submitForm = async (data: FormValues) => {
    const initial = initialRef.current
    if (!initial) return

    if ((currentGeometry?.features?.length ?? 0) === 0) {
      enqueueSnackbar('Please draw a property boundary before saving.', { variant: 'warning' })
      return
    }

    const payload: any = {}
    const name = data.name.trim()
    const reference = data.reference.trim()
    if (name !== (initial.name ?? '').trim()) payload.name = name
    if (reference !== (initial.reference ?? '').trim()) payload.reference = reference

    if (
      JSON.stringify(normalizeFeatureCollection(currentGeometry)) !==
      JSON.stringify(normalizeFeatureCollection(initialGeometryRef.current))
    ) {
      payload.geometry = normalizeFeatureCollection(currentGeometry)
    }

    if (!eqNumber(data.structure_value, initial.structure_value)) payload.structure_value = data.structure_value
    if (!eqNumber(data.contents_value, initial.contents_value)) payload.contents_value = data.contents_value
    if (!eqNumber(data.revenue, initial.revenue)) payload.revenue = data.revenue

    if (Object.keys(payload).length === 0) {
      enqueueSnackbar('No changes to save', { variant: 'info' })
      return
    }

    await api
      .patch(`/locations/${propertyId}`, payload)
      .then(res => {
        console.log('success', res)
        enqueueSnackbar('Property updated', { variant: 'success' })
        queryClient.invalidateQueries({ queryKey: [`/locations/${propertyId}`, getLocationParams] })
        const nextBaseline = { ...data, name, reference }
        initialRef.current = nextBaseline
        reset(nextBaseline)
        initialGeometryRef.current = normalizeFeatureCollection(currentGeometry) as any

        if (window.history.length > 1) {
          history.goBack()
        } else {
          history.push(siteMap.Property.getPath(propertyId))
        }
      })
      .catch(err => {
        console.error('error', err)
        enqueueSnackbar('Error updating property', { variant: 'error' })
      })
  }

  return (
    <Page
      title="Edit Property"
      size="lg"
      back
      PageActions={() => (
        <Stack gap={1} direction="row">
          <Button
            onClick={formSubmit(submitForm)}
            variant={hasChanges ? 'contained' : 'outlined'}
            disabled={
              isSubmitting ||
              isEditingGeometry ||
              isLoading ||
              isError ||
              !propertyId ||
              property === null ||
              baselineValues === null
            }>
            Save
          </Button>
        </Stack>
      )}>
      {isLoading ? (
        <Typography>Loading...</Typography>
      ) : isError ? (
        <Typography color="error">
          Failed to load property{(error as any)?.message ? `: ${(error as any).message}` : '.'}
        </Typography>
      ) : !propertyId ? (
        <Typography color="error">Missing property id.</Typography>
      ) : property === null ? (
        <Typography color="error">Property not found.</Typography>
      ) : (
        <Card elevation={0}>
          <CardContent>
            <Stack spacing={3}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Name"
                      fullWidth
                      {...register('name')}
                      error={!!errors.name}
                      helperText={errors.name?.message}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Reference"
                      fullWidth
                      {...register('reference')}
                      error={!!errors.reference}
                      helperText={errors.reference?.message}
                    />
                  </Grid>
                </Grid>
              </Paper>

              <FeatureGate feature="projections">
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ xs: 'flex-start', sm: 'baseline' }}
                    justifyContent="space-between">
                    <Typography variant="h6" gutterBottom>
                      Exposure
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Amounts in {currencyISO} ({currencySymbol})
                    </Typography>
                  </Stack>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Controller
                        name="structure_value"
                        control={control}
                        render={({ field }) => (
                          <LocalisedNumberTextField
                            name={field.name}
                            label="Structure value"
                            fullWidth
                            value={field.value}
                            onValueChange={field.onChange}
                            onBlur={field.onBlur}
                            inputRef={field.ref}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>,
                            }}
                            error={!!errors.structure_value}
                            helperText={errors.structure_value?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Controller
                        name="contents_value"
                        control={control}
                        render={({ field }) => (
                          <LocalisedNumberTextField
                            name={field.name}
                            label="Contents value"
                            fullWidth
                            value={field.value}
                            onValueChange={field.onChange}
                            onBlur={field.onBlur}
                            inputRef={field.ref}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>,
                            }}
                            error={!!errors.contents_value}
                            helperText={errors.contents_value?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Controller
                        name="revenue"
                        control={control}
                        render={({ field }) => (
                          <LocalisedNumberTextField
                            name={field.name}
                            label="Revenue"
                            fullWidth
                            value={field.value}
                            onValueChange={field.onChange}
                            onBlur={field.onBlur}
                            inputRef={field.ref}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>,
                            }}
                            error={!!errors.revenue}
                            helperText={errors.revenue?.message}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </FeatureGate>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <DrawMap
                  title="Boundary"
                  onChange={handleGeometryDraftChange}
                  onError={handleMapError}
                  initGeometry={currentGeometry ?? undefined}
                  editable={isEditingGeometry}
                  autoFitGeometry
                  headerActions={
                    isEditingGeometry ? (
                      <Stack direction="row" spacing={1}>
                        <Button size="small" variant="outlined" onClick={discardGeometryEdit}>
                          Discard
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={confirmGeometryEdit}
                          disabled={!draftHasGeometry}>
                          Confirm
                        </Button>
                      </Stack>
                    ) : (
                      <Button size="small" variant="outlined" onClick={startGeometryEdit}>
                        Edit
                      </Button>
                    )
                  }
                  singlePolygon
                />
                {(!isEditingGeometry ? (currentGeometry?.features?.length ?? 0) === 0 : !draftHasGeometry) && (
                  <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                    Please draw a property boundary.
                  </Typography>
                )}
              </Paper>
            </Stack>
          </CardContent>
        </Card>
      )}
    </Page>
  )
}

export default EditProperty
